import { CtaBand } from "../../../components/site/CtaBand";
import { PageHead } from "../../../components/site/PageHead";
import { SiteIcon, type SiteIconName } from "../../../components/site/SiteIcon";
import { BUSINESS, IMAGES } from "../../../content/site";

/** BR-WEB-040 — the director quote, verbatim from the legacy About page (as carried in the prototype). */
export const DIRECTOR_QUOTE =
  "We don’t work out of the back of a ute. Our whole focus is on providing our customers with quality products and exceptional service, and our display centre was designed to do just that. Our consultants are fully trained in all our products and the building process, so our customers get the right advice.";
export const DIRECTOR_ATTRIBUTION = "Paul Cooper, Director";

interface ValueCard {
  icon: SiteIconName;
  title: string;
  body: string;
}

/** BR-WEB-042 — exactly four cards, each restating only a claim the legacy About page makes. */
export const VALUE_CARDS: readonly ValueCard[] = [
  {
    icon: "house",
    title: "Local & family owned",
    body: "Locally owned and managed by the Cooper family, from the same South Street site since day one.",
  },
  {
    icon: "shield",
    title: "Licensed & accountable",
    body: `Members of the Master Builders Association of Queensland, holding current QBCC builder licensing (${BUSINESS.qbcc}). Every installer is a trained, qualified tradesman supervised on site by our licensed builder.`,
  },
  {
    icon: "award",
    title: "Proven products",
    body: "Only engineered products from manufacturers with a proven track record, reliable warranties and impeccable reputations.",
  },
  {
    icon: "hammer",
    title: "The full service",
    body: "Full supply and installation including council permits, ‘kit only’ supply pricing, and after-sales care and maintenance that is the envy of others.",
  },
];

/**
 * About — replaces legacy /about-us (Story #25443). Copy is the legacy About page with typo-only
 * edits, tightened without adding claims (baseline § 5); no year of establishment is shown (the
 * legacy site contradicts itself — BR-WEB-006). Static content: no network calls.
 */
export default function AboutPage() {
  return (
    <>
      <PageHead
        crumbs={[{ label: "About" }]}
        title={
          <>
            Locally owned. <em style={{ fontStyle: "italic", color: "var(--gold)" }}>Locally managed.</em>
          </>
        }
        lead="Multiple award winners for business excellence, design and manufacturing. Locally owned and locally managed — serving Toowoomba, the Darling Downs and the Lockyer Valley."
        bg={IMAGES.bannerAbout}
      />

      <section className="gw-sec" aria-labelledby="about-story">
        <div className="gw-wrap gw-story">
          <div>
            <p id="about-story" className="gw-eyebrow">
              Our story
            </p>
            <p>
              Greatway Patios, your outdoor living specialists, launched from humble beginnings in our
              showroom at {BUSINESS.street}, {BUSINESS.town} — and we still trade from the same convenient
              location. We’re locally owned and locally managed by the Cooper family, who have many years
              of renovation and construction experience.
            </p>
            <p>
              Stocking the largest range of outdoor living products on the Darling Downs, we can meet all
              your outdoor living needs. Since we started, we’ve only sourced engineered products from
              manufacturers with a proven track record, reliable warranties and impeccable reputations in
              the industry.
            </p>
            <p>
              We recognised many years ago that customers like to view and touch the products they’re
              interested in, and to see live displays and samples. So we built a stunning display centre
              showcasing all our products in one place, together with product samples and photographs of
              work across the Darling Downs. You’re welcome to walk around at your leisure.
            </p>
          </div>
          <div>
            <blockquote className="gw-pull" data-testid="director-quote">
              {DIRECTOR_QUOTE}
              <cite>{DIRECTOR_ATTRIBUTION}</cite>
            </blockquote>
          </div>
        </div>
      </section>

      <section className="gw-sec sand" aria-labelledby="about-values">
        <div className="gw-wrap">
          <div className="gw-sechead">
            <div>
              <p className="gw-eyebrow">What sets us apart</p>
              <h2 id="about-values" className="gw-h2">
                You can trust the quality and reliability of our work.
              </h2>
            </div>
          </div>
          <ul className="gw-values" data-testid="value-cards" style={{ listStyle: "none", padding: 0, margin: 0 }}>
            {VALUE_CARDS.map((v) => (
              <li key={v.title} className="gw-val">
                <SiteIcon name={v.icon} size={30} />
                <h3>{v.title}</h3>
                <p>{v.body}</p>
              </li>
            ))}
          </ul>
          <p id="about-area" className="gw-eyebrow" style={{ marginTop: 56 }}>
            Where we work
          </p>
          <ul
            className="gw-area"
            aria-labelledby="about-area"
            data-testid="service-area"
            style={{ listStyle: "none", padding: 0, marginBottom: 0 }}
          >
            {BUSINESS.serviceArea.map((place) => (
              <li key={place}>
                <span>{place}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <CtaBand
        title="Visit the display centre, or we’ll come to you."
        sub="Whether you’d like to walk through our display centre or have us visit your home, the team at Greatway Patios welcomes your contact."
      />
    </>
  );
}
